import {
  PDFDocument, StandardFonts, rgb, PDFOperator, PDFNumber, PDFPage, degrees,
  drawRectangle, drawSvgPath, drawObject, pushGraphicsState, popGraphicsState, translate,
  rectangle, clip, endPath,
} from 'pdf-lib';
import type { PDFFont, PDFImage, PDFRef, PDFName } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

// PDF operator for character spacing (Tc operator)
const setCharacterSpacing = (spacing: number) =>
  PDFOperator.of('Tc' as any, [PDFNumber.of(spacing)]);
import type {
  Template,
  TextElement,
  ImageElement,
  QrElement,
  BarcodeElement,
  ShapeElement,
  PdfLayoutPlan,
} from './types';
import type { BarcodeResult } from './barcode';
import { ensureArtworkRendererReady, resolveSvgColor, SvgRendererInitializationError, svgToPngRaster } from '../../../resvgRenderer';
import { decodeSvgBytes, SvgAssetRenderError } from './svgAsset';
import { generateQrVectorPath } from './qr';
export { generateQrDataUrl } from './qr';
export type { QrRenderOptions } from './qr';
export { generateBarcodeDataUrl } from './barcode';
export type { BarcodeRenderOptions, BarcodeResult } from './barcode';
export { convertSvgToPngDataUrl } from './svg';
export type { SvgRenderOptions } from './svg';

export type { Template, TextElement, ImageElement, QrElement, PdfLayoutPlan } from './types';

export type FontSource = {
  family: string;
  weight?: number | string;
  style?: 'normal' | 'italic';
  url?: string;
  data?: Uint8Array | ArrayBuffer | string;
};

export type ExportTemplateToPdfOptions = {
  template: Template;
  layout: PdfLayoutPlan;
  labelCount: number;
  qrValues: string[];
  qrIndexOffset: number;
  commonData: Record<string, any>;
  themeData: Record<string, any>;
  qrElements: QrElement[];
  /** QR cache: key → PNG data URL (string) or raw PNG bytes (Uint8Array) */
  qrCache: Record<string, string | Uint8Array>;
  /** Barcode cache: key → BarcodeResult with dataUrl as string or PNG bytes as Uint8Array */
  barcodeCache: Record<
    string,
    BarcodeResult | { pngBytes: Uint8Array; width: number; height: number }
  >;
  getQrKey: (value: string, el: QrElement, fg?: string, bg?: string) => string;
  getBarcodeKey: (value: string, el: BarcodeElement) => string;
  formatTextContent: (el: TextElement, data?: Record<string, any>) => string;
  resolveImageUrl: (el: ImageElement, data?: Record<string, any>) => string | undefined;
  resolveImageSource?: (el: ImageElement, data?: Record<string, any>) => {
    url?: string; asset?: ImageElement['imageAsset'];
  };
  resolveBarcodeData: (el: BarcodeElement, data?: Record<string, any>) => string;
  fetchImageBytes: (url: string) => Promise<Uint8Array | undefined>;
  fontSources?: FontSource[];
  fetchFontBytes?: (url: string) => Promise<Uint8Array | undefined>;
  resolveFontData?: (family: string, weight?: number | string) => Promise<Uint8Array | undefined>;
  resolveFontFamily?: (el: TextElement & Record<string, unknown>) => string; // Resolve effective font family for legacy fonts
  defaultFontFamily?: string;
  resolveElementColor?: (el: any, field: string) => string | undefined;
  isPreview?: boolean;
  /** Pre-rendered SVG shape cache: key (svgContent|width|height) → PNG bytes.
   *  When provided, skips the DOM-dependent dynamic import('./svg') path.
   *  Used by Web Workers where DOM APIs are unavailable. */
  svgShapeCache?: Record<string, Uint8Array>;
};

export type PdfExportResult = {
  pdfBytes: Uint8Array;
  blob: Blob;
  url?: string;
};

// At 72 DPI, pixels equal points directly (1 point = 1/72 inch).
// guard against NaN/Infinity/undefined so pdf-lib validators never receive invalid values.
const pxToPt = (px: number): number => (Number.isFinite(px) ? px : 0);

const decodeBase64 = (value: string) => {
  if (typeof atob === 'function') {
    const decoded = atob(value);
    return Uint8Array.from({ length: decoded.length }, (_, idx) => decoded.charCodeAt(idx));
  }
  if (typeof Buffer !== 'undefined') {
    return Uint8Array.from(Buffer.from(value, 'base64'));
  }
  throw new Error('No base64 decoder available');
};

const toUint8Array = (value?: Uint8Array | ArrayBuffer | string) => {
  if (!value) return undefined;
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (typeof value === 'string') {
    const base64 = value.startsWith('data:') ? value.split(',')[1] : value;
    return decodeBase64(base64);
  }
  return undefined;
};

const normalizeFontFamily = (value?: string) => {
  if (!value) return '';
  const [first] = value.split(',');
  return first.replace(/['"]/g, '').trim().toLowerCase();
};

const normalizeFontWeight = (weight?: number | string) => {
  if (weight === undefined || weight === null) return 400;
  if (typeof weight === 'number') return weight;
  const lower = weight.toLowerCase();
  if (lower === 'bold') return 700;
  if (lower === 'normal') return 400;
  const parsed = Number(weight);
  return Number.isFinite(parsed) ? parsed : 400;
};

const fontCacheKey = (family: string, weight: number, style = 'normal') =>
  `${family || 'default'}:${weight || 400}:${style}`;

const transparentPngBytes = decodeBase64(
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wIAAgMB9pMmVZkAAAAASUVORK5CYII=',
);

const parseColor = (value?: string) => {
  if (!value) return rgb(0, 0, 0);
  const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    const expand =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    const r = parseInt(expand.slice(0, 2), 16) / 255;
    const g = parseInt(expand.slice(2, 4), 16) / 255;
    const b = parseInt(expand.slice(4, 6), 16) / 255;
    return rgb(r, g, b);
  }
  const rgbMatch = value.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const parts = rgbMatch[1].split(',').map((p) => Number(p.trim()));
    if (parts.length >= 3 && parts.slice(0, 3).every((p) => Number.isFinite(p))) {
      return rgb(parts[0] / 255, parts[1] / 255, parts[2] / 255);
    }
  }
  return rgb(0, 0, 0);
};
const parseQrColor = async (value: string) => {
  const trimmed = value.trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) {
    return { color: parseColor(trimmed), opacity: 1 };
  }
  const rgbMatch = trimmed.match(/^rgb\(\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*\)$/i);
  if (rgbMatch) {
    const [red, green, blue] = rgbMatch.slice(1).map(channel => Math.min(255, Math.max(0, Number(channel))) / 255);
    return { color: rgb(red, green, blue), opacity: 1 };
  }
  await ensureArtworkRendererReady();
  const { red, green, blue, opacity } = resolveSvgColor(value);
  return { color: rgb(red, green, blue), opacity };
};

/**
 * Calculate dimensions that fit an image within a bounding box while preserving aspect ratio
 * (equivalent to CSS object-fit: contain)
 */
const calculateContainDimensions = (
  imgWidth: number,
  imgHeight: number,
  boxWidth: number,
  boxHeight: number,
): { width: number; height: number; offsetX: number; offsetY: number } => {
  if (!imgWidth || !imgHeight || !boxWidth || !boxHeight) {
    console.warn(
      `[PDF] calculateContainDimensions: degenerate dimensions img=${imgWidth}x${imgHeight} box=${boxWidth}x${boxHeight}, skipping fit`,
    );
    return { width: boxWidth || 0, height: boxHeight || 0, offsetX: 0, offsetY: 0 };
  }

  const imgRatio = imgWidth / imgHeight;
  const boxRatio = boxWidth / boxHeight;

  let width: number, height: number;

  if (imgRatio > boxRatio) {
    // Image is wider than box - fit to width
    width = boxWidth;
    height = boxWidth / imgRatio;
  } else {
    // Image is taller than box - fit to height
    height = boxHeight;
    width = boxHeight * imgRatio;
  }

  // Center the image within the bounding box
  const offsetX = (boxWidth - width) / 2;
  const offsetY = (boxHeight - height) / 2;

  return { width, height, offsetX, offsetY };
};

const pickFontSource = (family: string, weight: number, sources?: FontSource[], style = 'normal') => {
  if (!sources || !sources.length || !family) return undefined;
  const matchingFamily = sources.filter((src) => normalizeFontFamily(src.family) === family);
  const matchingStyle = matchingFamily.filter((src) => (src.style ?? 'normal') === style);
  const familySources = matchingStyle.length ? matchingStyle
    : matchingFamily.filter((src) => (src.style ?? 'normal') === 'normal');
  if (!familySources.length) return undefined;
  const exact = familySources.find((src) => normalizeFontWeight(src.weight) === weight);
  if (exact) return exact;
  const candidates = familySources.map((src) => ({
    source: src,
    weight: normalizeFontWeight(src.weight),
    distance: Math.abs(normalizeFontWeight(src.weight) - weight),
  }));
  candidates.sort((a, b) => a.distance - b.distance || a.weight - b.weight);
  return candidates[0]?.source;
};

const shouldUseSyntheticBold = (
  family: string | undefined,
  weight: number | string | undefined,
  sources?: FontSource[],
  style = 'normal',
) => {
  const normalizedFamily = normalizeFontFamily(family);
  const normalizedWeight = normalizeFontWeight(weight);
  if (!normalizedFamily || normalizedWeight < 600) {
    return false;
  }

  if (normalizedFamily.startsWith('asset-font-') || normalizedFamily.startsWith('legacy-font-')) {
    return true;
  }

  const requestedSource = pickFontSource(normalizedFamily, normalizedWeight, sources, style);
  const regularSource = pickFontSource(normalizedFamily, 400, sources, style);
  if (!requestedSource || !regularSource) {
    return false;
  }

  if (requestedSource === regularSource) {
    return true;
  }
  if (requestedSource.url && regularSource.url) {
    return requestedSource.url === regularSource.url;
  }
  if (requestedSource.data && regularSource.data) {
    return requestedSource.data === regularSource.data;
  }

  return false;
};

const isLikelySerif = (family: string) => family.includes('georgia') || family.includes('serif');
const isLikelyMono = (family: string) =>
  family.includes('mono') ||
  family.includes('plex') ||
  family.includes('courier') ||
  family.includes('consolas');

const getAlignmentOffset = (
  lineWidth: number,
  containerWidth: number,
  align: 'left' | 'center' | 'right',
): number => {
  switch (align) {
    case 'center':
      return (containerWidth - lineWidth) / 2;
    case 'right':
      return containerWidth - lineWidth;
    case 'left':
    default:
      return 0;
  }
};

const breakLongTokens = (text: string, maxTokenLength = 24, chunkSize = 12) => {
  if (!text) return text;
  const tokens = text.split(' ');
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i];
    if (t.length <= maxTokenLength) continue;
    const pieces: string[] = [];
    for (let j = 0; j < t.length; j += chunkSize) {
      pieces.push(t.slice(j, j + chunkSize));
    }
    tokens[i] = pieces.join(' ');
  }
  return tokens.join(' ');
};

const wrapLines = (
  font: PDFFont,
  text: string,
  maxWidthPt: number,
  fontSize: number,
  letterSpacingEm = 0,
) => {
  const lines: string[] = [];
  const paragraphs = text.replace(/\r/g, '').split('\n');
  // Letter spacing in em is relative to font size; adds space after each char except last
  const letterSpacingPt = letterSpacingEm * fontSize;
  const measure = (t: string) => {
    const baseWidth = font.widthOfTextAtSize(t, fontSize);
    // Add letter spacing for (n-1) characters (spacing is added after each char except the last)
    const extraSpacing = t.length > 0 ? letterSpacingPt * (t.length - 1) : 0;
    return baseWidth + extraSpacing;
  };

  paragraphs.forEach((para) => {
    //handle empty paragraphs (blank lines in source text)
    if (!para.trim()) {
      lines.push('');
      return;
    }
    const words = para.split(' ');
    let current = '';
    words.forEach((word) => {
      const candidate = current ? `${current} ${word}` : word;
      if (measure(candidate) <= maxWidthPt || !current) {
        current = candidate;
      } else {
        if (current) lines.push(current);
        current = word;
      }
    });
    if (current) lines.push(current);
  });

  return lines;
};

const resolvePdfFont = async (params: {
  doc: PDFDocument;
  cache: Map<string, PDFFont>;
  italicFonts: WeakSet<PDFFont>;
  family?: string;
  weight?: number | string;
  style?: 'normal' | 'italic';
  fontSources?: FontSource[];
  fetchFontBytes?: (url: string) => Promise<Uint8Array | undefined>;
  resolveFontData?: (family: string, weight?: number | string) => Promise<Uint8Array | undefined>;
  defaultFontFamily?: string;
}) => {
  const {
    doc,
    cache,
    italicFonts,
    family,
    weight,
    style = 'normal',
    fontSources,
    fetchFontBytes,
    resolveFontData,
    defaultFontFamily,
  } = params;
  const normalizedFamily =
    normalizeFontFamily(family) || normalizeFontFamily(defaultFontFamily) || '';
  const normalizedWeight = normalizeFontWeight(weight);
  const cacheKey = fontCacheKey(normalizedFamily, normalizedWeight, style);

  const fontFetcher =
    fetchFontBytes ||
    (typeof fetch === 'function'
      ? async (url: string) => {
          const res = await fetch(url);
          if (!res.ok) return undefined;
          const buffer = await res.arrayBuffer();
          return new Uint8Array(buffer);
        }
      : undefined);

  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const source = pickFontSource(normalizedFamily, normalizedWeight, fontSources, style);
  const sourceCacheKey = source?.url
    ? `${normalizedFamily}:source:${source.url}`
    : `${fontCacheKey(normalizedFamily, normalizedWeight, source?.style ?? 'normal')}:embedded-source`;
  const cachedSource = cache.get(sourceCacheKey);
  if (cachedSource) {
    cache.set(cacheKey, cachedSource);
    return cachedSource;
  }
  let fontBytes = toUint8Array(source?.data);
  if (!fontBytes && source?.url && fontFetcher) {
    fontBytes = await fontFetcher(source.url).catch(() => undefined);
  }
  if (!fontBytes && resolveFontData) {
    fontBytes = await resolveFontData(family || defaultFontFamily || '', weight);
  }
  if (fontBytes) {
    try {
      const embedded = await doc.embedFont(fontBytes, { subset: false, features: { liga: false } });
      cache.set(cacheKey, embedded);
      cache.set(sourceCacheKey, embedded);
      if (source?.style === 'italic') italicFonts.add(embedded);
      return embedded;
    } catch (e) {
      // Font embedding failed (likely unsupported format like WOFF/WOFF2)
      // Fall through to use standard fallback font
      console.warn(`[PDF] Failed to embed font "${family}":`, e instanceof Error ? e.message : e);
    }
  }

  const isBold = normalizedWeight >= 600;
  const isItalic = style === 'italic';
  const fallbackName = (() => {
    if (isLikelySerif(normalizedFamily)) {
      if (isItalic) return isBold ? StandardFonts.TimesRomanBoldItalic : StandardFonts.TimesRomanItalic;
      return isBold ? StandardFonts.TimesRomanBold : StandardFonts.TimesRoman;
    }
    if (isLikelyMono(normalizedFamily)) {
      if (isItalic) return isBold ? StandardFonts.CourierBoldOblique : StandardFonts.CourierOblique;
      return isBold ? StandardFonts.CourierBold : StandardFonts.Courier;
    }
    if (isItalic) return isBold ? StandardFonts.HelveticaBoldOblique : StandardFonts.HelveticaOblique;
    return isBold ? StandardFonts.HelveticaBold : StandardFonts.Helvetica;
  })();

  const fallbackCacheKey = fontCacheKey(normalizeFontFamily(fallbackName), normalizedWeight);
  const cachedFallback = cache.get(fallbackName) || cache.get(fallbackCacheKey);
  if (cachedFallback) {
    cache.set(cacheKey, cachedFallback);
    return cachedFallback;
  }

  const embedded = await doc.embedFont(fallbackName);
  if (isItalic) italicFonts.add(embedded);
  cache.set(fallbackName, embedded);
  cache.set(fallbackCacheKey, embedded);
  cache.set(cacheKey, embedded);
  return embedded;
};

// Apply rotation transformation matrix around specified center point
const applyRotationTransform = (
  page: PDFPage,
  angleDegrees: number,
  centerX: number,
  centerY: number,
) => {
  if (!Number.isFinite(angleDegrees) || !Number.isFinite(centerX) || !Number.isFinite(centerY)) return;
  if (!angleDegrees) return; // Skip if no rotation

  const angleRad = (angleDegrees * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  // Calculate translation components for rotation around (centerX, centerY)
  const tx = centerX - centerX * cos + centerY * sin;
  const ty = centerY - centerX * sin - centerY * cos;

  // Save graphics state
  page.pushOperators(PDFOperator.of('q' as any));

  // Apply transformation matrix: [a b c d e f] cm
  page.pushOperators(
    PDFOperator.of('cm' as any, [
      PDFNumber.of(cos),
      PDFNumber.of(sin),
      PDFNumber.of(-sin),
      PDFNumber.of(cos),
      PDFNumber.of(tx),
      PDFNumber.of(ty),
    ]),
  );
};

const restoreGraphicsState = (page: PDFPage) => {
  page.pushOperators(PDFOperator.of('Q' as any));
};

// Apply label rotation transform. Rotates around the label's center,
// then translates so the rotated bounding box's top-left is at the original grid position.
// Parameters are in PDF coordinates (Y increases upward).
const applyLabelRotationTransform = (
  page: PDFPage,
  rotation: 0 | 90 | 180 | 270,
  topLeftX: number,
  topLeftY: number,
  labelWidthPt: number,
  labelHeightPt: number,
) => {
  if (!rotation) return;

  // Save graphics state
  page.pushOperators(PDFOperator.of('q' as any));

  // We want clockwise rotation in screen space.
  // In PDF, positive angles rotate counter-clockwise, so we negate.
  const angleRad = (-rotation * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  // Original center of the label in PDF coordinates
  const centerX = topLeftX + labelWidthPt / 2;
  const centerY = topLeftY - labelHeightPt / 2;

  // After rotation, dimensions swap for 90° and 270°
  const newWidth = rotation === 90 || rotation === 270 ? labelHeightPt : labelWidthPt;
  const newHeight = rotation === 90 || rotation === 270 ? labelWidthPt : labelHeightPt;

  // After rotating around the center, the new bounding box top-left would be at:
  //   newTopLeftX = centerX - newWidth/2
  //   newTopLeftY = centerY + newHeight/2
  // We want it to be at (topLeftX, topLeftY), so we need to translate by:
  //   deltaX = topLeftX - (centerX - newWidth/2) = topLeftX - centerX + newWidth/2
  //   deltaY = topLeftY - (centerY + newHeight/2) = topLeftY - centerY - newHeight/2
  const deltaX = topLeftX - centerX + newWidth / 2;
  const deltaY = topLeftY - centerY - newHeight / 2;

  // Combined transformation: T(delta) * T(center) * R(angle) * T(-center)
  // Which simplifies to rotation around center, then translate by delta
  const tx = centerX + deltaX - centerX * cos + centerY * sin;
  const ty = centerY + deltaY - centerX * sin - centerY * cos;

  page.pushOperators(
    PDFOperator.of('cm' as any, [
      PDFNumber.of(cos),
      PDFNumber.of(sin),
      PDFNumber.of(-sin),
      PDFNumber.of(cos),
      PDFNumber.of(tx),
      PDFNumber.of(ty),
    ]),
  );
};

export const exportTemplateToPdf = async (
  options: ExportTemplateToPdfOptions,
): Promise<PdfExportResult> => {
  const {
    template,
    layout,
    labelCount,
    qrValues,
    qrIndexOffset,
    commonData,
    themeData,
    qrElements,
    barcodeCache,
    getQrKey,
    getBarcodeKey,
    formatTextContent,
    resolveImageUrl,
    resolveBarcodeData,
    fetchImageBytes,
    fontSources,
    fetchFontBytes,
    resolveFontData,
    resolveFontFamily,
    defaultFontFamily,
    isPreview,
    svgShapeCache,
    resolveElementColor,
  } = options;

  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  const fontCache = new Map<string, PDFFont>();
  const italicFonts = new WeakSet<PDFFont>();
  const helvetica = await doc.embedFont(StandardFonts.Helvetica);
  fontCache.set(StandardFonts.Helvetica, helvetica);
  fontCache.set(fontCacheKey('helvetica', 400), helvetica);

  // Cache images to avoid re-fetching and re-embedding for repeated elements
  const imageBytesCache = new Map<string, Uint8Array>();
  const imageEmbedCache = new Map<string, { image: PDFImage; svgViewport?: { width: number; height: number } }>();
  // Cache parsed vector commands in document-scoped forms, not in each label's content stream.
  const qrVectorCache = new Map<string, PDFRef>();
  const qrColorCache = new Map<string, Awaited<ReturnType<typeof parseQrColor>>>();
  const barcodeEmbedCache = new Map<string, PDFImage>();
  const svgEmbedCache = new Map<string, PDFImage>();
  const TRANSPARENT_KEY = '__transparent__';
  const transparentImage = await doc.embedPng(transparentPngBytes);
  imageEmbedCache.set(TRANSPARENT_KEY, { image: transparentImage });

  const getEmbeddedImage = async (url?: string, targetWidth?: number) => {
    const key = url || TRANSPARENT_KEY;
    const cached = imageEmbedCache.get(key);
    if (cached) return cached;

    let bytes: Uint8Array | undefined =
      key === TRANSPARENT_KEY ? transparentPngBytes : imageBytesCache.get(key);
    if (!bytes && url) {
      bytes = await fetchImageBytes(url);
      if (bytes) imageBytesCache.set(key, bytes);
    }

    const usableBytes = bytes || transparentPngBytes;

    // detect image type from bytes instead of trusting URL/MIME type. this ensures that the pdf can resolve these assets
    const isJpg =
      usableBytes.length >= 3 &&
      usableBytes[0] === 0xff &&
      usableBytes[1] === 0xd8 &&
      usableBytes[2] === 0xff;
    const svgContent = decodeSvgBytes(usableBytes);
    const embedKey = svgContent ? `${key}:${targetWidth || 'intrinsic'}` : key;
    const cachedEmbed = imageEmbedCache.get(embedKey);
    if (cachedEmbed) return cachedEmbed;

    let img: PDFImage;
    let svgViewport: { width: number; height: number } | undefined;
    if (isJpg) {
      img = await doc.embedJpg(usableBytes);
    } else if (svgContent) {
      await ensureArtworkRendererReady();
      try {
        const raster = svgToPngRaster(svgContent, targetWidth ? targetWidth * 4 : undefined);
        img = await doc.embedPng(raster.pngBytes);
        svgViewport = { width: raster.intrinsicWidth, height: raster.intrinsicHeight };
      } catch {
        // Do not expose markup, data URLs, or signed URLs through UI/worker errors.
        throw new SvgAssetRenderError();
      }
    } else {
      img = await doc.embedPng(usableBytes);
    }
    const embedded = { image: img, svgViewport };
    imageEmbedCache.set(embedKey, embedded);
    return embedded;
  };

  // Use original page dimensions - rotation will be applied via page.setRotation()
  const pageWidthPt = pxToPt(layout?.pageWidthPx || 1);
  const pageHeightPt = pxToPt(layout?.pageHeightPx || 1);
  const sortedElements =
    template.elements?.slice().sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)) || [];

  const getQrValueForIndex = (globalIdx: number) => {
    const qrIdx = globalIdx + qrIndexOffset;
    const qrValue = qrValues.length
      ? (qrValues[qrIdx] ?? qrValues[qrValues.length - 1])
      : (qrElements[0]?.data as string | undefined) || `QR-${qrIdx}`;
    return { qrIdx, qrValue };
  };

  const actualPageCount = Math.max(1, Math.ceil(labelCount / layout.labelsPerPage));

  for (let pageIdx = 0; pageIdx < actualPageCount; pageIdx += 1) {
    const page = doc.addPage([pageWidthPt, pageHeightPt]);
    const qrPageResources = new Map<PDFRef, PDFName>();

    // Apply page rotation if specified
    if (layout.pageRotation) {
      page.setRotation(degrees(layout.pageRotation));
    }

    //draw dashed blue outlines at every label position on this page (preview only).
    if (isPreview) {
      const bwPt = pxToPt(layout.labelAreaWidthPx);
      const bhPt = pxToPt(layout.labelAreaHeightPx);
      const isMatchLabelSize =
        layout.labelsPerPage === 1 &&
        Math.abs(pageWidthPt - bwPt) < 1 &&
        Math.abs(pageHeightPt - bhPt) < 1;
      if (!isMatchLabelSize) {
        const dashArray = doc.context.obj([PDFNumber.of(4), PDFNumber.of(3)]);
        for (const pos of layout.positions.slice(0, layout.labelsPerPage)) {
          const bx = pxToPt(pos.x);
          const by = pageHeightPt - pxToPt(pos.y) - bhPt;
          page.pushOperators(
            PDFOperator.of('q' as any),
            PDFOperator.of('d' as any, [dashArray, PDFNumber.of(0)]),
            PDFOperator.of('w' as any, [PDFNumber.of(0.75)]),
            PDFOperator.of('RG' as any, [
              PDFNumber.of(0.2),
              PDFNumber.of(0.4),
              PDFNumber.of(1.0),
            ]),
            PDFOperator.of('re' as any, [
              PDFNumber.of(bx),
              PDFNumber.of(by),
              PDFNumber.of(bwPt),
              PDFNumber.of(bhPt),
            ]),
            PDFOperator.of('S' as any),
            PDFOperator.of('Q' as any),
          );
        }
      }
    }

    const start = pageIdx * layout.labelsPerPage;
    const tiles = layout.positions
      .slice(0, layout.labelsPerPage)
      .map((pos, idx) => ({ pos, globalIdx: start + idx }))
      .filter((t) => t.globalIdx < labelCount);

    for (const tile of tiles) {
      const { qrIdx, qrValue } = getQrValueForIndex(tile.globalIdx);
      const labelData = {
        ...commonData,
        ...themeData,
        qrIndex: qrIdx,
        qrValue,
        labelIndex: tile.globalIdx + qrIndexOffset,
      };

      const xPt = pxToPt(tile.pos.x);
      const yPt = pxToPt(tile.pos.y);
      // Use the layout's label area dimensions as authoritative. The layout defines the
      // physical label size on the sheet; template content is positioned within this area.
      // Previously Math.max(template.width, ...) was used, which could cause labels to
      // overflow into adjacent positions when the template canvas was larger than the label.
      const wPt = pxToPt(layout.labelAreaWidthPx);
      const hPt = pxToPt(layout.labelAreaHeightPx);

      // Apply label rotation if specified (rotate entire label before layout positioning)
      const labelRotation = layout.labelRotation;
      if (labelRotation) {
        // Top-left in PDF coords (Y increases upward)
        const topLeftY = pageHeightPt - yPt;
        applyLabelRotationTransform(page, labelRotation, xPt, topLeftY, wPt, hPt);
      }

      page.drawRectangle({
        x: xPt,
        y: pageHeightPt - yPt - hPt,
        width: wPt,
        height: hPt,
        color: parseColor(!isPreview ? '#ffffff' : template.background || '#ffffff'),
      });

      for (const el of sortedElements) {
        const elW = 'width' in el ? (el as ImageElement | QrElement).width : 0;
        const elH = 'height' in el ? (el as ImageElement | QrElement | BarcodeElement).height : 0;

        if (!Number.isFinite(el.x))
          console.warn(
            `[PDF] Element "${el.id}" (${el.type}): non-finite x=${el.x}, defaulting to 0`,
          );
        if (!Number.isFinite(el.y))
          console.warn(
            `[PDF] Element "${el.id}" (${el.type}): non-finite y=${el.y}, defaulting to 0`,
          );

        const ex = xPt + pxToPt(el.x);
        const top = pageHeightPt - yPt - pxToPt(el.y);

        if (el.type === 'text') {
          const textEl = el as TextElement;
          const content = formatTextContent(textEl, labelData);
          const paddingX = 0;
          const paddingY = 0;

          if (!Number.isFinite(textEl.fontSize))
            console.warn(
              `[PDF] Element "${el.id}" (text): non-finite fontSize=${textEl.fontSize}, defaulting to 12`,
            );
          const fontSize = Number.isFinite(textEl.fontSize) ? textEl.fontSize : 12;
          const lineHeightFactor = textEl.lineHeight ?? 1;
          const sx = textEl.scaleX ?? 1;
          const sy = textEl.scaleY ?? 1;
          const hasScale = sx !== 1 || sy !== 1;
          const maxWidthPt = pxToPt(Math.max(20, textEl.width - paddingX * 2) / sx);
          // Use resolveFontFamily callback if provided (for legacy fonts), otherwise use element's fontFamily
          const effectiveFontFamily = resolveFontFamily
            ? resolveFontFamily(textEl)
            : textEl.fontFamily;
          const fontForEl = await resolvePdfFont({
            doc,
            cache: fontCache,
            italicFonts,
            family: effectiveFontFamily,
            weight: textEl.fontWeight,
            style: textEl.fontStyle,
            fontSources,
            fetchFontBytes,
            resolveFontData,
            defaultFontFamily,
          });
          const useSyntheticBold = shouldUseSyntheticBold(
            effectiveFontFamily,
            textEl.fontWeight,
            fontSources,
            textEl.fontStyle,
          );
          const useSyntheticItalic = textEl.fontStyle === 'italic' && !italicFonts.has(fontForEl);
          const pdfHeightAtCssSize = fontForEl.heightAtSize(fontSize);
          const renderSizeFactor = pdfHeightAtCssSize ? fontSize / pdfHeightAtCssSize : 1;
          const renderFontSize = fontSize * renderSizeFactor;
          const syntheticBoldOffset = useSyntheticBold
            ? Math.max(0.2, renderFontSize * 0.018)
            : 0;

          // Letter spacing: convert em to points for PDF
          const letterSpacingEm = textEl.letterSpacing || 0;
          const letterSpacingPt = letterSpacingEm * renderFontSize;

          const lines = wrapLines(fontForEl, content, maxWidthPt, renderFontSize, letterSpacingEm);
          const lineHeightPt = pxToPt(fontSize) * lineHeightFactor;
          const ascentApprox = fontSize * 0.8;
          const firstBaseline = top - pxToPt(paddingY) - ascentApprox;
          const color = parseColor(options.resolveElementColor?.(textEl, 'colorConditions') || textEl.color);

          // Apply rotation if set - rotate around element center to match designer
          const textWidth = pxToPt(textEl.width);
          const textHeight = (lines.length * lineHeightPt + pxToPt(paddingY) * 2) * sy;
          const rotationCenterX = ex + textWidth / 2;
          const rotationCenterY = top - textHeight / 2;
          if (textEl.rotation) {
            // Negate angle to account for Y-axis flip (CSS rotates clockwise, PDF counter-clockwise)
            applyRotationTransform(page, -textEl.rotation, rotationCenterX, rotationCenterY);
          }

          if (hasScale) {
            page.pushOperators(PDFOperator.of('q' as any));
            page.pushOperators(
              PDFOperator.of('cm' as any, [
                PDFNumber.of(sx),
                PDFNumber.of(0),
                PDFNumber.of(0),
                PDFNumber.of(sy),
                PDFNumber.of(ex * (1 - sx)),
                PDFNumber.of(top * (1 - sy)),
              ]),
            );
          }

          // Apply character spacing if set (Tc operator in PDF)
          if (letterSpacingPt !== 0) {
            page.pushOperators(setCharacterSpacing(letterSpacingPt));
          }

          lines.forEach((line, idx) => {
            // Calculate line width for alignment (same formula as wrapLines measure)
            const lineBaseWidth = fontForEl.widthOfTextAtSize(line, renderFontSize);
            const lineExtraSpacing = line.length > 0 ? letterSpacingPt * (line.length - 1) : 0;
            const lineWidth = lineBaseWidth + lineExtraSpacing;
            const alignOffset = getAlignmentOffset(lineWidth, maxWidthPt, textEl.align);
            const lineX = ex + alignOffset;
            const syntheticBoldHalfOffset = syntheticBoldOffset / 2;

            page.drawText(line, {
              x: useSyntheticBold ? lineX - syntheticBoldHalfOffset : lineX,
              y: firstBaseline - lineHeightPt * idx,
              size: renderFontSize,
              font: fontForEl,
              color,
              ...(useSyntheticItalic ? { ySkew: degrees(12) } : {}),
            });

            if (useSyntheticBold) {
              page.drawText(line, {
                x: lineX + syntheticBoldHalfOffset,
                y: firstBaseline - lineHeightPt * idx,
                size: renderFontSize,
                font: fontForEl,
                color,
                ...(useSyntheticItalic ? { ySkew: degrees(12) } : {}),
              });
            }
            if (textEl.textDecoration === 'underline' && lineWidth > 0) {
              const underlineY = firstBaseline - lineHeightPt * idx - fontSize * 0.1;
              page.drawLine({
                start: { x: lineX, y: underlineY },
                end: { x: lineX + lineWidth, y: underlineY },
                thickness: Math.max(0.5, fontSize * 0.05),
                color,
              });
            }
          });

          // Reset character spacing to 0 after drawing text with custom spacing
          if (letterSpacingPt !== 0) {
            page.pushOperators(setCharacterSpacing(0));
          }

          if (hasScale) {
            page.pushOperators(PDFOperator.of('Q' as any));
          }

          // Restore graphics state if rotation was applied
          if (textEl.rotation) {
            restoreGraphicsState(page);
          }
        } else if (el.type === 'qr') {
          const qrEl = el as QrElement;
          const resolvedFg = resolveElementColor?.(qrEl, 'fgConditions') || qrEl.fg;
          const resolvedBg = resolveElementColor?.(qrEl, 'bgConditions') || qrEl.bg;
          const key = getQrKey(qrValue, qrEl, resolvedFg, resolvedBg);
          const w = pxToPt(qrEl.width || elW);
          const h = pxToPt(qrEl.height || elH);
          let formRef = qrVectorCache.get(key);
          if (!formRef) {
            const path = await generateQrVectorPath({
              value: qrValue, width: qrEl.width, height: qrEl.height,
              marginPx: qrEl.margin, fg: resolvedFg, bg: resolvedBg,
              dpi: template.dpi ?? 72, errorCorrectionLevel: qrEl.errorCorrectionLevel,
            });
            const background = resolvedBg || '#ffffff';
            const foreground = resolvedFg || '#000000';
            for (const value of [background, foreground]) {
              if (!qrColorCache.has(value)) qrColorCache.set(value, await parseQrColor(value));
            }
            const backgroundColor = qrColorCache.get(background)!;
            const foregroundColor = qrColorCache.get(foreground)!;
            const form = doc.context.formXObject([
              ...drawRectangle({
                x: 0, y: 0, width: w, height: h, color: backgroundColor.color,
                borderColor: undefined, borderWidth: 0,
                rotate: degrees(0), xSkew: degrees(0), ySkew: degrees(0),
                graphicsState: 'Background',
              }),
              ...drawSvgPath(path, {
                x: 0, y: h, color: foregroundColor.color, borderWidth: 0,
                borderColor: undefined, scale: 1, graphicsState: 'Foreground',
              }),
            ], {
              BBox: [0, 0, w, h],
              Resources: {
                ExtGState: {
                  Background: { Type: 'ExtGState', ca: backgroundColor.opacity },
                  Foreground: { Type: 'ExtGState', ca: foregroundColor.opacity },
                },
              },
            });
            formRef = doc.context.register(form);
            qrVectorCache.set(key, formRef);
          }
          let resourceName = qrPageResources.get(formRef);
          if (!resourceName) {
            resourceName = page.node.newXObject('QR', formRef);
            qrPageResources.set(formRef, resourceName);
          }
          if (qrEl.rotation) applyRotationTransform(page, -qrEl.rotation, ex + w / 2, top - h / 2);
          page.pushOperators(pushGraphicsState(), translate(ex, top - h), drawObject(resourceName), popGraphicsState());
          if (qrEl.rotation) restoreGraphicsState(page);
        } else if (el.type === 'image') {
          const imgEl = el as ImageElement;
          const source = options.resolveImageSource?.(imgEl, labelData);
          const resolvedUrl = source ? source.url : resolveImageUrl(imgEl, labelData);
          const boxWidth = pxToPt(imgEl.width || elW);
          const boxHeight = pxToPt(imgEl.height || elH);
          const embedded = await getEmbeddedImage(resolvedUrl, imgEl.width || elW).catch((error) => {
            if (error instanceof SvgRendererInitializationError) throw error;
            if (error instanceof SvgAssetRenderError) {
              const artwork = (source ? source.asset : imgEl.imageAsset)?.displayName || imgEl.id;
              throw new Error(`Could not render SVG artwork "${artwork}". Check the SVG file and try again.`);
            }
            return { image: transparentImage, svgViewport: undefined };
          });
          const img = embedded.image;

          // Calculate actual render dimensions based on fit mode
          let renderWidth = boxWidth;
          let renderHeight = boxHeight;
          let renderOffsetX = 0;
          let renderOffsetY = 0;

          const fitMode = imgEl.fit ?? 'contain';
          if (fitMode === 'contain' && img && img !== transparentImage) {
            // SVG viewport geometry stays precise even when its cached PNG axes round.
            const naturalWidth = embedded.svgViewport?.width ?? img.width;
            const naturalHeight = embedded.svgViewport?.height ?? img.height;

            if (naturalWidth > 0 && naturalHeight > 0) {
              const fit = calculateContainDimensions(
                naturalWidth,
                naturalHeight,
                boxWidth,
                boxHeight,
              );
              renderWidth = fit.width;
              renderHeight = fit.height;
              renderOffsetX = fit.offsetX;
              renderOffsetY = fit.offsetY;
            }
          }
          // SVG cover crops the shared original embed at draw time. Raster-cover keeps
          // its existing stretch policy; never infer asset format from URL or MIME.
          const svgViewport = embedded.svgViewport;
          const clipSvgCover = fitMode === 'cover' && svgViewport &&
            [svgViewport.width, svgViewport.height, boxWidth, boxHeight].every(value => Number.isFinite(value) && value > 0);
          if (clipSvgCover) {
            const scale = Math.max(boxWidth / svgViewport.width, boxHeight / svgViewport.height);
            renderWidth = svgViewport.width * scale;
            renderHeight = svgViewport.height * scale;
            if (![renderWidth, renderHeight].every(Number.isFinite)) {
              throw new Error('SVG cover geometry exceeds finite PDF bounds');
            }
            renderOffsetX = (boxWidth - renderWidth) / 2;
            renderOffsetY = (boxHeight - renderHeight) / 2;
          }

          const renderX = ex + renderOffsetX;
          // PDF Y is bottom-up: top - boxHeight puts us at the box bottom, then add offsetY to move up
          const renderY = top - boxHeight + renderOffsetY;

          // Apply rotation if set - rotate around bounding box center to match designer
          const rotationCenterX = ex + boxWidth / 2;
          const rotationCenterY = top - boxHeight / 2;
          const hasRotation = Boolean(imgEl.rotation) && (!clipSvgCover || Number.isFinite(imgEl.rotation));
          if (hasRotation) {
            // Negate angle to account for Y-axis flip (CSS rotates clockwise, PDF counter-clockwise)
            applyRotationTransform(page, -imgEl.rotation!, rotationCenterX, rotationCenterY);
          }
          try {
            if (clipSvgCover) page.pushOperators(PDFOperator.of('q' as any));
            try {
              if (clipSvgCover) {
                page.pushOperators(rectangle(ex, top - boxHeight, boxWidth, boxHeight), clip(), endPath());
              }
              const safeOpacity = Number.isFinite(imgEl.opacity) ? imgEl.opacity : undefined;
              if (safeOpacity !== imgEl.opacity)
                console.warn(
                  `[PDF] Element "${el.id}" (image): non-finite opacity=${imgEl.opacity}, defaulting to undefined`,
                );
              page.drawImage(img, {
                x: renderX,
                y: renderY,
                width: renderWidth,
                height: renderHeight,
                opacity: safeOpacity,
              });
            } finally {
              if (clipSvgCover) restoreGraphicsState(page);
            }
          } finally {
            if (hasRotation) restoreGraphicsState(page);
          }
        } else if (el.type === 'barcode') {
          const bcEl = el as BarcodeElement;
          const resolvedData = resolveBarcodeData(bcEl, labelData);
          const key = getBarcodeKey(resolvedData, bcEl);
          const cached = barcodeCache[key];
          if (cached) {
            let png = barcodeEmbedCache.get(key);
            if (!png) {
              // Support both legacy BarcodeResult (dataUrl string) and worker path (pngBytes Uint8Array)
              const pngData =
                'pngBytes' in cached ? cached.pngBytes : (cached as BarcodeResult).dataUrl;
              png = await doc.embedPng(pngData as any);
              barcodeEmbedCache.set(key, png);
            }
            const h = pxToPt(bcEl.height);
            // Use cached width for consistency with designer
            const w = pxToPt(cached.width);

            const rotationCenterX = ex;
            const rotationCenterY = top;
            if (bcEl.rotation) {
              // Negate angle to account for Y-axis flip (CSS rotates clockwise, PDF counter-clockwise)
              applyRotationTransform(page, -bcEl.rotation, rotationCenterX, rotationCenterY);
            }

            page.drawImage(png, { x: ex, y: top - h, width: w, height: h });

            // Restore graphics state if rotation was applied
            if (bcEl.rotation) {
              restoreGraphicsState(page);
            }
          } else {
            console.warn(`[PDF] Barcode element skipped: no cached data for key "${key}"`);
          }
        } else if (el.type === 'shape') {
          const shape = el as ShapeElement;
          const strokeColor = parseColor(options.resolveElementColor?.(shape, 'strokeColorConditions') || shape.strokeColor);
          const strokeWidth = shape.strokeWidth || 1;

          const v1x = ex + pxToPt(shape.v1.x);
          const v1y = top - pxToPt(shape.v1.y);
          const v2x = ex + pxToPt(shape.v2.x);
          const v2y = top - pxToPt(shape.v2.y);

          if (shape.shapeType === 'line') {
            page.drawLine({
              start: { x: v1x, y: v1y },
              end: { x: v2x, y: v2y },
              thickness: strokeWidth,
              color: strokeColor,
            });
          } else if (shape.shapeType === 'rectangle' && shape.v3 && shape.v4) {
            const v3x = ex + pxToPt(shape.v3.x);
            const v3y = top - pxToPt(shape.v3.y);
            const v4x = ex + pxToPt(shape.v4.x);
            const v4y = top - pxToPt(shape.v4.y);

            const resolvedFill = options.resolveElementColor?.(shape, 'fillColorConditions') || shape.fillColor;
            const hasFill = resolvedFill && resolvedFill !== 'transparent';
            const fillColor = hasFill ? parseColor(resolvedFill) : undefined;

            page.pushOperators(PDFOperator.of('q' as any));

            page.pushOperators(
              PDFOperator.of('RG' as any, [
                PDFNumber.of(strokeColor.red),
                PDFNumber.of(strokeColor.green),
                PDFNumber.of(strokeColor.blue),
              ]),
            );

            page.pushOperators(PDFOperator.of('w' as any, [PDFNumber.of(strokeWidth)]));

            if (fillColor) {
              page.pushOperators(
                PDFOperator.of('rg' as any, [
                  PDFNumber.of(fillColor.red),
                  PDFNumber.of(fillColor.green),
                  PDFNumber.of(fillColor.blue),
                ]),
              );
            }

            page.pushOperators(
              PDFOperator.of('m' as any, [PDFNumber.of(v1x), PDFNumber.of(v1y)]), // moveto v1
              PDFOperator.of('l' as any, [PDFNumber.of(v2x), PDFNumber.of(v2y)]), // lineto v2
              PDFOperator.of('l' as any, [PDFNumber.of(v3x), PDFNumber.of(v3y)]), // lineto v3
              PDFOperator.of('l' as any, [PDFNumber.of(v4x), PDFNumber.of(v4y)]), // lineto v4
              PDFOperator.of('h' as any),
            );

            if (fillColor) {
              page.pushOperators(PDFOperator.of('B' as any));
            } else {
              page.pushOperators(PDFOperator.of('S' as any));
            }

            page.pushOperators(PDFOperator.of('Q' as any));
          } else if (shape.shapeType === 'svg' && shape.svgContent && shape.width && shape.height) {
            const svgCacheKey = `${shape.svgContent}|${shape.width}|${shape.height}`;
            let pngImage = svgEmbedCache.get(svgCacheKey);
            if (!pngImage) {
              // Check pre-rendered SVG cache first (worker-safe path)
              const preRenderedPng = svgShapeCache?.[svgCacheKey];
              if (preRenderedPng) {
                pngImage = await doc.embedPng(preRenderedPng);
              } else {
                // Fallback to DOM-based rendering (main thread only)
                const { convertSvgToPngDataUrl } = await import('./svg');
                const pngDataUrl = await convertSvgToPngDataUrl({
                  svgContent: shape.svgContent,
                  width: shape.width,
                  height: shape.height,
                });

                // Extract base64 data from data URL
                const base64Data = pngDataUrl.split(',')[1];
                const pngBytes = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
                pngImage = await doc.embedPng(pngBytes);
              }
              svgEmbedCache.set(svgCacheKey, pngImage);
            }

            const widthPt = pxToPt(shape.width);
            const heightPt = pxToPt(shape.height);

            page.drawImage(pngImage, {
              x: ex,
              y: top - heightPt,
              width: widthPt,
              height: heightPt,
            });
          }
        }
      }

      // Restore graphics state if label rotation was applied
      if (labelRotation) {
        restoreGraphicsState(page);
      }
    }

    if (isPreview) {
      const watermarkText = 'PREVIEW USE ONLY';
      const watermarkFontSize = Math.min(pageWidthPt, pageHeightPt) / 12;
      const textWidth = helvetica.widthOfTextAtSize(watermarkText, watermarkFontSize);

      const centerX = pageWidthPt / 2;
      const centerY = pageHeightPt / 2;

      page.pushOperators(PDFOperator.of('q' as any));

      const angleRad = (-45 * Math.PI) / 180;
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);
      const tx = centerX - centerX * cos + centerY * sin;
      const ty = centerY - centerX * sin - centerY * cos;

      page.pushOperators(
        PDFOperator.of('cm' as any, [
          PDFNumber.of(cos),
          PDFNumber.of(sin),
          PDFNumber.of(-sin),
          PDFNumber.of(cos),
          PDFNumber.of(tx),
          PDFNumber.of(ty),
        ]),
      );

      page.drawText(watermarkText, {
        x: centerX - textWidth / 2,
        y: centerY - watermarkFontSize / 2,
        size: watermarkFontSize,
        font: helvetica,
        color: rgb(1, 0, 0),
        opacity: 0.3,
      });

      page.pushOperators(PDFOperator.of('Q' as any));
    }
  }

  const pdfBytes = await doc.save();
  const pdfBuffer = pdfBytes.buffer instanceof ArrayBuffer
    ? new Uint8Array(pdfBytes.buffer, pdfBytes.byteOffset, pdfBytes.byteLength)
    : new Uint8Array(pdfBytes);
  const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
  const url =
    typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
      ? URL.createObjectURL(blob)
      : undefined;

  return { pdfBytes: pdfBuffer, blob, url };
};
